import { HEEDIFY_PROVISIONING_BASE_URL, TEXT_TO_SPEECH_URL } from "../Config/Config";
import axios from "axios";

const getHeaders = (token, isMultipart = false) => {
  // Remove "Bearer " prefix if already present to avoid double prefix
  const cleanToken = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return {
    Authorization: `Bearer ${cleanToken}`,
    "Content-Type": isMultipart ? "multipart/form-data" : "application/json",
  };
};

// ------------------ LICENSES ------------------
export async function getAllLicences(tenantId, token, scope) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/LicencesManagement/${tenantId}/${scope}/get`;

    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

// ------------------ ROLES ------------------
export async function getAllRoles(tenant, token) {
  if (!tenant || !token) throw new Error("Tenant and token are required");
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/RolesManagement/${tenant}/get/ALL`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data?.success ? response.data.roles : {};
  } catch (e) {
    return {};
  }
}

// ------------------ PERMISSIONS ------------------
export async function getAllPermissions(tenantId, token) {
  try {

    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/Permissions/${tenantId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    if (response.data?.success) {
      return {
        userId: response.data.userId,
        tenant: response.data.tenant,
        scopes: response.data.scopes,
        roles: response.data.roles,
        // Le backend renvoie `partnerAccess: { partnerTenantId, accessMode }` en accès revendeur.
        // Il était jeté ici : l'interface ne pouvait donc pas savoir qu'elle regardait le tenant
        // d'un client en lecture seule, et devait le DÉDUIRE des permissions — ce qui échoue
        // précisément dans le cas limite (client sans aucune entité).
        partnerAccess: response.data.partnerAccess || null,
      };
    }
    return { scopes: {}, roles: {}, partnerAccess: null };
  } catch (e) {
    return { scopes: {}, roles: {} };
  }
}

// ------------------ PARTNER TENANTS ------------------
export async function getPartnerTenants(tenantId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/partnerTenantsManagement/${tenantId}/get/ALL`;
    const response = await axios.get(url, { headers: getHeaders(token) });

    if (response.data?.success) {
      // Nouvelle structure: { myTenant: {...}, tenants: [...] }
      return response.data.data || { myTenant: null, tenants: [] };
    }
    return { myTenant: null, tenants: [] };
  } catch (e) {
    return { myTenant: null, tenants: [] };
  }
}

// ------------------ MEDIA ------------------
export async function getAllMedia(tenantId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AudioManagement/${tenantId}/default/get/All`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

export async function deleteMedia(tenantId, mediaName, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AudioManagement/${tenantId}/default/delete/${mediaName}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function uploadMedia(tenantId, userId, file, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/UploadMedia/${tenantId}/default/?userid=${userId}&filename=${file.name}`;
    const formData = new FormData();
    formData.append("File", file);
    const response = await axios.post(url, formData, { headers: getHeaders(token, true) });
    return response.data;
  } catch (e) {
    return null;
  }
}

// ------------------ AGENTS ------------------
export async function AddAgent(tenantId, userId, scope, agent, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentManagement/${tenantId}/${scope}/add/agent?userid=${userId}`;
    const response = await axios.post(url, { agent }, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("❌ Error adding agent:", e?.response?.data || e.message);
    return e?.response?.data || { success: false, message: e.message };
  }
}

export async function UpdateAgent(tenantId, userId, agentId, scope, agent, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentManagement/${tenantId}/${scope}/update/${agentId}?userid=${userId}`;
    const response = await axios.post(url, { agent }, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("❌ Error updating agent:", e?.response?.data || e.message);
    return e?.response?.data || { success: false, message: e.message };
  }
}

export async function DeleteAgent(tenantId, userId, agentId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentManagement/${tenantId}/${scope}/remove/${agentId}?userid=${userId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (error) {
    console.error("❌ Error deleting agent:", error.response?.data || error.message);
    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to delete agent"
    };
  }
}

export async function GetAgent(tenantId, userId, agentId, token, scope = "default") {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentManagement/${tenantId}/${encodeURIComponent(scope)}/get/${agentId}?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function GetAllAgent(tenantId, userId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentManagement/${tenantId}/${scope}/get/ALL?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

// ------------------ AGENT PROFILES ------------------
export async function AddAgentProfile(tenantId, agentProfile, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentProfilesManagement/${tenantId}/add/NULL`;
    const response = await axios.post(url, agentProfile, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function UpdateAgentProfile(tenantId, agentProfileId, agentProfile, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentProfilesManagement/${tenantId}/update/${agentProfileId}`;
    const response = await axios.post(url, agentProfile, { headers: getHeaders(token) });

    return response.data;
  } catch (e) {
    return null;
  }
}

export async function DeleteAgentProfile(tenantId, agentProfileId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentProfilesManagement/${tenantId}/remove/${agentProfileId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });

    return response.data;
  } catch (e) {
    return null;
  }
}

export async function GetAllAgentProfile(tenantId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentProfilesManagement/${tenantId}/get/ALL`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

export async function GetAgentProfile(tenantId, agentProfileId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentProfilesManagement/${tenantId}/get/${agentProfileId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

// ------------------ ANALYTICS PROFILES ------------------
export async function AddAnalyticsProfile(tenantId, analyticsProfile, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AnalyticsProfileManagement/${tenantId}/add/NULL`;
    const response = await axios.post(url, analyticsProfile, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function UpdateAnalyticsProfile(tenantId, analyticsProfileId, analyticsProfile, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AnalyticsProfileManagement/${tenantId}/update/${analyticsProfileId}`;
    const response = await axios.post(url, analyticsProfile, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    // Ne pas avaler l'erreur en `null` : renvoyer un objet exploitable pour que l'appelant
    // distingue un 403 (droits insuffisants) d'un échec réseau/serveur et le signale à l'user.
    // (`success:false` se comporte comme `null` pour les tests existants `result?.success`.)
    return {
      success: false,
      status: e?.response?.status,
      message: e?.response?.data?.message || e?.message || 'Request failed',
    };
  }
}

export async function DeleteAnalyticsProfile(tenantId, analyticsProfileId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AnalyticsProfileManagement/${tenantId}/remove/${analyticsProfileId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function GetAllAnalyticsProfiles(tenantId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AnalyticsProfileManagement/${tenantId}/get/ALL`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

export async function GetAnalyticsProfile(tenantId, analyticsProfileId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AnalyticsProfileManagement/${tenantId}/get/${analyticsProfileId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    console.log("✅ Analytics profile retrieved successfully:", response.data);
    return response.data;
  } catch (e) {
    // Le CODE HTTP est conservé. En le jetant (`return null`), la sauvegarde d'un widget —
    // qui relit le profil avant de le réécrire — ne pouvait plus distinguer un token expiré
    // (401) d'un manque de droits (403) ou d'une panne : l'utilisateur recevait « réessayez »
    // et recliquait indéfiniment sur un Save qui ne pouvait plus aboutir.
    console.error("❌ GetAnalyticsProfile failed:", e.response?.status, e.message);
    return { success: false, status: e.response?.status, message: e.message };
  }
}

// ------------------ CALLFLOW ------------------
export async function GetCallFlow(tenantId, userId, CallFlowId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CallFlowManagement/${tenantId}/${scope}/get/${CallFlowId}?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });

    return response.data;
  } catch (e) {
    return null;
  }
}

export async function AddCallFlow(tenantId, userId, scope, token, callFlow) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CallFlowManagement/${tenantId}/${scope}/add/null?userid=${userId}`;
    const response = await axios.post(url, { callFlow }, { headers: getHeaders(token) });
    console.log("✅ Call flow added successfully:", response.data);
    return response.data;
  } catch (e) {
    console.error("❌ Error adding call flow:", e?.response?.data || e.message);
    return e?.response?.data || { success: false, message: e.message };
  }
}

export async function UpdateCallFlow(tenantId, userId, CallFlowId, scope, token, callFlow) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CallFlowManagement/${tenantId}/${scope}/update/${CallFlowId}?userid=${userId}`;
    const response = await axios.post(url, { callFlow }, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("❌ Error updating call flow:", e?.response?.data || e.message);
    return e?.response?.data || { success: false, message: e.message };
  }
}

export async function DeleteCallFlow(tenantId, userId, CallFlowId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CallFlowManagement/${tenantId}/${scope}/remove/${CallFlowId}?userid=${userId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("❌ Error deleting call flow:", e?.response?.data || e.message);
    return e?.response?.data || { success: false, message: e.message };
  }
}

export async function GetAllCallFlow(tenantId, userId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CallFlowManagement/${tenantId}/${scope}/get/ALL?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

// ------------------ CHATFLOW ------------------
export async function GetChatFlow(tenantId, userId, chatFlowId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ChatFlowManagement/${tenantId}/${scope}/get/${chatFlowId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function AddChatFlow(tenantId, userId, scope, token, chatFlow) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ChatFlowManagement/${tenantId}/${scope}/add/${chatFlow.id}`;
    const response = await axios.post(url, chatFlow, { headers: getHeaders(token) });
    // Retourner la réponse complète avec success et chatFlow
    return response.data;
  } catch (e) {
    return { success: false, message: e?.response?.data?.message || e.message };
  }
}

export async function UpdateChatFlow(tenantId, userId, chatFlowId, scope, token, chatFlow) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ChatFlowManagement/${tenantId}/${scope}/update/${chatFlowId}`;
    const response = await axios.post(url, chatFlow, { headers: getHeaders(token) });
    // Retourner la réponse complète avec success et chatFlow
    return response.data;
  } catch (e) {
    return { success: false, message: e?.response?.data?.message || e.message };
  }
}

export async function DeleteChatFlow(tenantId, userId, chatFlowId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ChatFlowManagement/${tenantId}/${scope}/delete/${chatFlowId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function GetAllChatFlow(tenantId, userId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ChatFlowManagement/${tenantId}/${scope}/get/ALL`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

export async function getChatFlowChannel(tenantId, scope, flowId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ChannelManagement/${tenantId}/${scope}/${flowId}/get/ALL`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

export async function unAssigneChannel(tenantId, scope, flowId, channelId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ChannelManagement/${tenantId}/${scope}/${flowId}/unAssigneChannel/${channelId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function assigneChannel(tenantId, scope, flowId, channelId, token, payload) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ChannelManagement/${tenantId}/${scope}/${flowId}/assigneChannel/${channelId}`;
    const response = await axios.post(url, payload, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

// ------------------ QUEUES ------------------
export async function getAllQueue(tenantId, userId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/QueueManagement/${tenantId}/${scope}/get/ALL?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

export async function GetQueue(tenantId, userId, queueId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/QueueManagement/${tenantId}/${scope}/get/${queueId}?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    // Contrat conservé (null) : trois appelants s'appuient dessus. Mais la CAUSE est tracée —
    // sans elle, un 403, un 404 et une coupure réseau donnaient le même écran muet, et il
    // fallait capturer un HAR pour les distinguer.
    console.error(`GetQueue a échoué [${e?.response?.status || "réseau"}] tenant=${tenantId} scope=${scope} id=${queueId}`);
    return null;
  }
}

export async function AddQueue(tenantId, userId, scope, queue, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/QueueManagement/${tenantId}/${scope}/add/queue?userid=${userId}`;
    const response = await axios.post(url, { queue }, { headers: getHeaders(token) });
    return response;
  } catch (e) {
    return { data: null, status: 500 };
  }
}

export async function UpdateQueue(tenantId, userId, queueId, scope, queue, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/QueueManagement/${tenantId}/${scope}/update/${queueId}?userid=${userId}`;
    const response = await axios.post(url, { queue }, { headers: getHeaders(token) });
    return response;
  } catch (e) {
    return { data: null, status: 500 };
  }
}

export async function DeleteQueue(tenantId, userId, queueId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/QueueManagement/${tenantId}/${scope}/remove/${queueId}?userid=${userId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}
// ------------------ GROUPS ------------------
export async function getAllGroups(tenantId, scope, userId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/GroupManagement/${tenantId}/${scope}/get/ALL?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

export async function GetGroup(tenantId, scope, groupId, userId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/GroupManagement/${tenantId}/${scope}/get/${groupId}?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    // Cf. GetQueue : contrat conservé (null), cause tracée.
    console.error(`GetGroup a échoué [${e?.response?.status || "réseau"}] tenant=${tenantId} scope=${scope} id=${groupId}`);
    return null;
  }
}

export async function AddGroup(tenantId, scope, userId, group, token) {
  try {

    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/GroupManagement/${tenantId}/${scope}/create/null?userid=${userId}`;
    const response = await axios.post(url, group, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function UpdateGroup(tenantId, scope, userId, groupId, group, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/GroupManagement/${tenantId}/${scope}/update/${groupId}?userid=${userId}`;
    const response = await axios.post(url, group, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function DeleteGroup(tenantId, scope, groupId, userId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/GroupManagement/${tenantId}/${scope}/remove/${groupId}?userid=${userId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

// ------------------ AUDIO GENERATION ------------------
export const generateAudioService = async (data) => {
  try {
    const response = await axios.post(TEXT_TO_SPEECH_URL, data);
    const url = decodeURIComponent(response.data.url);
    return url;
  } catch (e) {
    return null;
  }
};

// ------------------ SCOPES ------------------
export async function getScopeById(tenantId, scopeId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ScopeManagement/${tenantId}/get/${scopeId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });

    // Vérifier si la réponse a un wrapper success/data
    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function getAllScopes(tenantId, token) {

  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ScopeManagement/${tenantId}/get/ALL`;
    const response = await axios.get(url, { headers: getHeaders(token) });

    return response.data;
  } catch (e) {
    return [];
  }
}

export async function addScope(tenantId, token, scope) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ScopeManagement/${tenantId}/add`;
    const response = await axios.post(url, scope, { headers: getHeaders(token) });
    return response;
  } catch (e) {
    console.error("❌ Error adding scope:", e?.response?.data || e.message);
    return { data: e?.response?.data || { success: false, message: e.message } };
  }
}

export async function updateScope(tenantId, scopeId, token, scope) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ScopeManagement/${tenantId}/update/${scopeId}`;
    const response = await axios.post(url, scope, { headers: getHeaders(token) });
    return response;
  } catch (e) {
    console.error("❌ Error updating scope:", e?.response?.data || e.message);
    return { data: e?.response?.data || { success: false, message: e.message } };
  }
}

export async function deleteScope(tenantId, scopeId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ScopeManagement/${tenantId}/remove/${scopeId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response;
  } catch (e) {
    console.error("❌ Error deleting scope:", e?.response?.data || e.message);
    return { data: e?.response?.data || { success: false, message: e.message } };
  }
}

// ------------------ CONSENT ------------------
/**
 * URL de consentement administrateur, ADRESSÉE AU TENANT QU'ON REGARDE.
 *
 * Elle pointait sur `/common` : Microsoft résolvait alors le tenant sur le COMPTE CONNECTÉ, pas
 * sur celui affiché dans le portail. Un administrateur multi-tenants — ou un revendeur consultant
 * un client — accordait donc le consentement dans SON PROPRE annuaire Entra, sans erreur ni
 * avertissement, en croyant configurer l'autre. Un succès au mauvais endroit, ce qui est plus
 * dangereux qu'un refus.
 *
 * Avec le tenant dans le chemin, Entra évalue le compte connecté CONTRE CE TENANT : s'il n'y est
 * pas administrateur, il refuse explicitement. Pour le cas courant — un administrateur de son
 * propre tenant — le comportement est rigoureusement identique.
 *
 * @param {string} clientId    application à consentir
 * @param {string} redirectUri retour, doit être enregistré sur l'application
 * @param {string} tenantId    tenant VISÉ (ACTIVE_TENANT). Repli sur `common` s'il manque, pour
 *                             ne pas fabriquer une URL invalide — le contrôle au retour
 *                             (GlobalSettingsComponent) reste alors la dernière barrière.
 */
export function getAdminConsentUrl(clientId, redirectUri, tenantId) {
  const authority = tenantId || "common";
  return `https://login.microsoftonline.com/${authority}/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}`;
}

export async function checkConsentStatusApi(tenantId, userId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CheckAppConsent/${tenantId}/getConsent`;
    const response = await axios.post(url, {
      tenantId: tenantId,
      userId: userId,
    }, { headers: getHeaders(token) });
    return response.data; // 200 → { IsAdmin: true, consentedApp: [...] }
  } catch (e) {
    // Le backend renvoie 400 { IsAdmin: false } quand le tenant n'a AUCUN consent
    // (= onboarding nécessaire). On renvoie ce corps pour distinguer ce cas d'une
    // vraie erreur réseau/serveur (→ null, on ne déduit rien).
    if (e.response && e.response.status === 400) {
      return e.response.data;
    }
    return null;
  }
}

export async function registerConsentApi(tenantId, userId, clientId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AppConsent/${tenantId}/add/${clientId}/${userId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

// ------------------ RECORDINGS ------------------
// Service to retrieve call recording info
export const retrieveCallRecordingInfo = (tenantId, filters) => {
  return new Promise((resolve) => {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/ManageRecordedCallCDR/${tenantId}/get`;
    axios.post(url, { filters })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        resolve();
      });
  });
};

// Service to retrieve call recording URL
export const retrieveCallRecordingUrl = (tenantId, recordingScopeId, callId) => {
  return new Promise((resolve) => {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/BlobSasGenerator/${tenantId}/${recordingScopeId}/${callId}`;
    axios.get(url)
      .then((response) => {
        if (response.data && response.data.sasUrl) {
          resolve(response.data.sasUrl);
          console.log("✅ Recording URL retrieved successfully:", response.data.sasUrl);
        } else {
          resolve(null);
        }
      })
      .catch((error) => {
        resolve();
      });
  });
};

// Service to get blob access token for an agent
export const getBlobAccessToken = (tenantId, agentScope, recordingScope, userId) => {
  return new Promise((resolve) => {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/Callrecordingsaccess/${tenantId}/${agentScope}/${recordingScope}?userid=${userId}`;
    axios.get(url)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        resolve();
      });
  });
};


// ------------------ AGENT AI ------------------
export async function GetAgentAI(tenantId, userId, agentAIId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/IaAgentManagement/${tenantId}/${scope}/get/${agentAIId}?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function AddAgentAI(tenantId, userId, scope, token, agentAI) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/IaAgentManagement/${tenantId}/${scope}/add/null?userid=${userId}`;
    const response = await axios.post(url, { iaAgent: agentAI }, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function UpdateAgentAI(tenantId, userId, agentAIId, scope, token, agentAI) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/IaAgentManagement/${tenantId}/${scope}/update/${agentAIId}?userid=${userId}`;
    const response = await axios.post(url, { iaAgent: agentAI }, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function DeleteAgentAI(tenantId, userId, agentAIId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/IaAgentManagement/${tenantId}/${scope}/remove/${agentAIId}?userid=${userId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return null;
  }
}

export async function GetAllAgentAI(tenantId, userId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/IaAgentManagement/${tenantId}/${scope}/get/ALL?userid=${userId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    return [];
  }
}

// ------------------ PORTAL SETTINGS MANAGEMENT ------------------

export async function getPortalSettings(tenantId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/PortalSettingsManagement/${tenantId}/get`;
    const response = await axios.get(url, { headers: getHeaders(token) });

    if (response.data?.success) {
      return {
        success: true,
        data: response.data.data
      };
    }

    return {
      success: false,
      data: null,
      message: response.data?.message || 'Failed to get portal settings'
    };
  } catch (error) {
    console.error('Error getting portal settings:', error);
    return {
      success: false,
      data: null,
      message: error.message
    };
  }
}
export async function addPortalSettings(tenantId, portalSettings, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/PortalSettingsManagement/${tenantId}/add`;
    const response = await axios.post(url, portalSettings, { headers: getHeaders(token) });

    if (response.data?.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Settings added successfully'
      };
    }

    return {
      success: false,
      message: response.data?.message || 'Failed to add settings'
    };
  } catch (error) {
    console.error('Error adding portal settings:', error);
    return {
      success: false,
      message: error.message
    };
  }
}


export async function updatePortalSettings(tenantId, portalSettings, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/PortalSettingsManagement/${tenantId}/default/update`;
    const response = await axios.post(url, portalSettings, { headers: getHeaders(token) });

    if (response.data?.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Settings updated successfully'
      };
    }

    return {
      success: false,
      message: response.data?.message || 'Failed to update settings'
    };
  } catch (error) {
    console.error('Error updating portal settings:', error);
    return {
      success: false,
      message: error.message
    };
  }
}
// ------------------ VARIABLES (CustomerDB) ------------------
export async function getAllVariables(tenantId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CustomerDBManagement/${tenantId}/${scope}/get/ALL`;

    const response = await axios.get(url, { headers: getHeaders(token) });

    return response.data?.success ? response.data.data : [];
  } catch (e) {
    console.error("Error fetching variables:", e);
    return [];
  }
}
export async function getVariable(tenantId, scope, variableId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CustomerDBManagement/${tenantId}/${scope}/get/${variableId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });

    return response.data?.success ? response.data.data : null;
  } catch (e) {
    console.error("Error fetching variable:", e);
    return null;
  }
}

export async function createVariable(tenantId, scope, variableData, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CustomerDBManagement/${tenantId}/${scope}/create/null`;
    const response = await axios.post(url, variableData, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("Error creating variable:", e);
    return { success: false, message: e.response?.data?.message || "Error creating variable" };
  }
}

export async function updateVariable(tenantId, scope, variableId, variableData, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CustomerDBManagement/${tenantId}/${scope}/update/${variableId}`;
    const response = await axios.post(url, variableData, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("Error updating variable:", e);
    return { success: false, message: e.response?.data?.message || "Error updating variable" };
  }
}

export async function deleteVariable(tenantId, scope, variableId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/CustomerDBManagement/${tenantId}/${scope}/remove/${variableId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("Error deleting variable:", e);
    return { success: false, message: e.response?.data?.message || "Error deleting variable" };
  }
}

// ------------------ WORKFLOWS ------------------
export async function getAllWorkflows(tenantId, scope, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/workflowManagement/${tenantId}/${scope}/get/ALL`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data?.success ? response.data.data : [];
  } catch (e) {
    console.error("Error fetching workflows:", e);
    return [];
  }
}

export async function getWorkflow(tenantId, scope, workflowId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/workflowManagement/${tenantId}/${scope}/get/${workflowId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data?.success ? response.data.data : null;
  } catch (e) {
    console.error("Error fetching workflow:", e);
    return null;
  }
}

export async function createWorkflow(tenantId, scope, workflowData, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/workflowManagement/${tenantId}/${scope}/create/null`;
    const response = await axios.post(url, workflowData, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("Error creating workflow:", e);
    return { success: false, message: e.response?.data?.message || "Error creating workflow" };
  }
}

export async function updateWorkflow(tenantId, scope, workflowId, workflowData, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/workflowManagement/${tenantId}/${scope}/update/${workflowId}`;
    const response = await axios.post(url, workflowData, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("Error updating workflow:", e);
    return { success: false, message: e.response?.data?.message || "Error updating workflow" };
  }
}

export async function deleteWorkflow(tenantId, scope, workflowId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/workflowManagement/${tenantId}/${scope}/remove/${workflowId}`;
    const response = await axios.post(url, {}, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("Error deleting workflow:", e);
    return { success: false, message: e.response?.data?.message || "Error deleting workflow" };

  }
}

// ------------------ AGENT PERFORMANCE ------------------
export async function getMyAgentProfile(tenantId, userId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/MyAgentProfile/${tenantId}/${userId}`;
    // const url = `${HEEDIFY_PROVISIONING_BASE_URL}/MyAgentProfile/${tenantId}/${userId}?token=${token}`;
    const response = await axios.get(url, { headers: getHeaders(token) });

    return response.data;
  } catch (e) {
    console.error("Error fetching agent profile:", e);
    return null;
  }
}

export async function getAgentConnectedCalls(tenantId, agentId, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/GetAgentConnectedCalls/${tenantId}/${agentId}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    return response.data;
  } catch (e) {
    console.error("Error fetching agent connected calls:", e);
    return null;
  }
}

export async function getAgentInformation(tenantId, profileScope, agentId, tz, tasks, token) {
  try {
    const url = `${HEEDIFY_PROVISIONING_BASE_URL}/AgentPerformance/${tenantId}/${profileScope}/${agentId}?tz=${tz}&tasks=${tasks}`;
    const response = await axios.get(url, { headers: getHeaders(token) });
    console.log("Agent information response:", response.data);
    return response.data;
  } catch (e) {
    console.error("Error fetching agent information:", e);
    return null;
  }
}


// ------------------ MAIL FLOWS ------------------
//
// Configuration des flux mail (MailFlowManagement). À la différence des autres appels de ce
// fichier, les erreurs ne sont PAS avalées en `null` : le service refuse délibérément une
// configuration inerte — règle sans file, file sans canal mail, boîte déjà prise — et son
// message est la seule chose qui dise à l'administrateur ce qu'il doit corriger. Le renvoyer
// tel quel à l'appelant est le but.

export async function getMailFlows(tenantId, scope, token) {
  const url = `${HEEDIFY_PROVISIONING_BASE_URL}/MailFlowManagement/${tenantId}?scope=${encodeURIComponent(scope || "default")}`;
  const response = await axios.get(url, { headers: getHeaders(token) });
  return response.data?.success ? response.data.mailFlows || [] : [];
}

export async function saveMailFlow(tenantId, mailFlow, token) {
  const url = `${HEEDIFY_PROVISIONING_BASE_URL}/MailFlowManagement/${tenantId}`;
  try {
    const response = await axios.post(url, mailFlow, { headers: getHeaders(token) });
    return { success: true, mailFlow: response.data?.mailFlow };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || e.message };
  }
}

export async function deleteMailFlow(tenantId, mailFlowId, token) {
  const url = `${HEEDIFY_PROVISIONING_BASE_URL}/MailFlowManagement/${tenantId}/${mailFlowId}`;
  try {
    await axios.delete(url, { headers: getHeaders(token) });
    return { success: true };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || e.message };
  }
}

// ------------------ MAIL TEMPLATES ------------------
//
// `mode=admin` est indispensable ici : la lecture ordinaire ne renvoie que du HTML DÉJÀ
// SUBSTITUÉ (c'est ce dont la console a besoin), et on n'édite pas un gabarit dont `{{agentName}}`
// est déjà remplacé par un nom — le réenregistrer figerait ce nom pour tout le monde. Le mode
// administrateur renvoie le corps brut, et seulement les gabarits PARTAGÉS : le gabarit personnel
// d'un agent est son brouillon de travail, pas une ressource à administrer.

export async function getSharedMailTemplates(tenantId, scope, token) {
  const url = `${HEEDIFY_PROVISIONING_BASE_URL}/MailTemplates/${tenantId}?mode=admin&scope=${encodeURIComponent(scope || "default")}`;
  const response = await axios.get(url, { headers: getHeaders(token) });
  return response.data?.success ? response.data.templates || [] : [];
}

export async function saveMailTemplate(tenantId, template, token) {
  const url = `${HEEDIFY_PROVISIONING_BASE_URL}/MailTemplates/${tenantId}`;
  try {
    // `shared: true` fait poser `ownerAgentId = null` côté serveur, qui revérifie que l'appelant
    // est administrateur du scope. L'écran ne crée jamais de gabarit personnel.
    const response = await axios.post(url, { ...template, shared: true }, { headers: getHeaders(token) });
    return { success: true, template: response.data?.template };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || e.message };
  }
}

export async function deleteMailTemplate(tenantId, templateId, token) {
  const url = `${HEEDIFY_PROVISIONING_BASE_URL}/MailTemplates/${tenantId}/${templateId}`;
  try {
    await axios.delete(url, { headers: getHeaders(token) });
    return { success: true };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || e.message };
  }
}

// ------------------ SMS TEMPLATES (Quick SMS) ------------------
//
// Même découpage que les gabarits mail : `mode=admin` rend le corps BRUT et seulement les
// gabarits PARTAGÉS. La réponse porte en plus une `measure` — encodage, longueur, segments —
// calculée par le MÊME code que celui de l'envoi. C'est la seule façon d'éviter qu'un écran
// annonce un segment là où la passerelle en facture deux.

export async function getSharedSmsTemplates(tenantId, scope, token) {
  const url = `${HEEDIFY_PROVISIONING_BASE_URL}/SmsTemplates/${tenantId}?mode=admin&scope=${encodeURIComponent(scope || "default")}`;
  const response = await axios.get(url, { headers: getHeaders(token) });
  return response.data?.success ? response.data.templates || [] : [];
}

export async function saveSmsTemplate(tenantId, template, token) {
  const url = `${HEEDIFY_PROVISIONING_BASE_URL}/SmsTemplates/${tenantId}`;
  try {
    const response = await axios.post(url, { ...template, shared: true }, { headers: getHeaders(token) });
    return { success: true, template: response.data?.template };
  } catch (e) {
    // Le serveur refuse un gabarit trop long en nommant le nombre de segments, l'encodage et les
    // caractères fautifs. Ce message EST l'information utile : le remplacer par « échec de
    // l'enregistrement » obligerait l'administrateur à deviner.
    return { success: false, message: e.response?.data?.message || e.message };
  }
}

export async function deleteSmsTemplate(tenantId, templateId, token) {
  const url = `${HEEDIFY_PROVISIONING_BASE_URL}/SmsTemplates/${tenantId}/${templateId}`;
  try {
    await axios.delete(url, { headers: getHeaders(token) });
    return { success: true };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || e.message };
  }
}
